import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { CheckCircle, Loader2 } from "lucide-react";

// ─── tokens ──────────────────────────────────────────────────────────────────

const T = {
  pageBg:    "#0f0f10",
  cardBg:    "#1c1c1e",
  inputBg:   "#151517",
  border:    "rgba(255,255,255,0.07)",
  accent:    "#34d399",
  accentDim: "rgba(52,211,153,0.12)",
  textPrimary:   "#f1f5f9",
  textSecondary: "#94a3b8",
  textMuted:     "#475569",
  radius:    "16px",
  radiusSm:  "12px",
};

const EASE     = [0.22, 1, 0.36, 1];
const DURATION = 0.5;

// ─── labour table data ───────────────────────────────────────────────────────

const LABOUR = [
  { work: "Integration and migration",    guided: "Pabloo", managed: "Pabloo" },
  { work: "Rate and rule design",         guided: "You",    managed: "Pabloo" },
  { work: "Campaign build",               guided: "You",    managed: "Pabloo" },
  { work: "Weekly issuance review",       guided: "You",    managed: "Pabloo" },
  { work: "Dormancy and float monitoring",guided: "You",    managed: "Pabloo" },
  { work: "Quarterly strategy call",      guided: "Pabloo", managed: "Pabloo" },
  { work: "Economy Review document",      guided: "You",    managed: "Pabloo" },
  { work: "Stack changes and replatforms",guided: "Pabloo", managed: "Pabloo" },
];

// ─── sub-components ──────────────────────────────────────────────────────────

function Divider() {
  return (
    <div
      style={{
        height: "1px",
        background: T.border,
        margin: "0 auto",
        width: "100%",
      }}
    />
  );
}

function SectionWrap({ children, style }) {
  return (
    <div
      style={{
        maxWidth: "860px",
        margin: "0 auto",
        padding: "72px 24px",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── main page ───────────────────────────────────────────────────────────────

export default function GuidedEconomy() {
  const navigate              = useNavigate();
  const [brand, setBrand]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const b = params.get("brand");
    if (b && b.trim()) setBrand(b.trim());
  }, []);

  const brandName = brand || "you";

  async function handleStart() {
    if (loading || sent) return;
    setLoading(true);

    const payload = {
      brand:       brand || null,
      tier:        "guided",
      source:      "managed_call",
      referrer:    document.referrer,
      accepted_at: new Date().toISOString(),
    };

    // Mock API call
    await new Promise((r) => setTimeout(r, 600));
    console.log("GuidedEconomy payload:", payload);

    setLoading(false);
    setSent(true);
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Guided Economy — Pabloo</title>
      </Helmet>

      <div style={{ background: T.pageBg, minHeight: "100vh" }}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION, ease: EASE }}
        >

          {/* ── Block 1 — Header ────────────────────────────────────────── */}
          <SectionWrap>
            <p
              className="text-xs font-bold uppercase tracking-widest mb-5"
              style={{ color: T.accent, letterSpacing: "0.12em" }}
            >
              Pabloo Guided Economy
            </p>

            <h1
              className="font-bold mb-5"
              style={{
                color: T.textPrimary,
                fontSize: "clamp(2rem, 5vw, 3rem)",
                lineHeight: 1.15,
                fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
              }}
            >
              The version {brandName} runs.
            </h1>

            <p
              className="mb-8"
              style={{
                color: T.textSecondary,
                fontSize: "1.0625rem",
                lineHeight: 1.7,
                maxWidth: "560px",
                fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
              }}
            >
              Same platform. Same ledger. Same rules engine. The difference is
              who operates it, and that is the only difference.
            </p>

            <div
              className="inline-flex items-baseline gap-1"
              style={{
                background: T.accentDim,
                border: `1px solid rgba(52,211,153,0.2)`,
                borderRadius: T.radiusSm,
                padding: "10px 18px",
              }}
            >
              <span
                style={{
                  color: T.accent,
                  fontSize: "1.75rem",
                  fontWeight: 700,
                  fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
                }}
              >
                $900
              </span>
              <span
                style={{
                  color: T.textMuted,
                  fontSize: "0.875rem",
                  fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
                }}
              >
                / month
              </span>
            </div>
          </SectionWrap>

          <Divider />

          {/* ── Block 2 — The one difference ────────────────────────────── */}
          <SectionWrap>
            <h2
              className="font-semibold mb-8"
              style={{
                color: T.textPrimary,
                fontSize: "1.25rem",
                fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
              }}
            >
              The one difference
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              {/* Guided first on mobile, Managed second */}
              <div
                className="order-1 md:order-2"
                style={{
                  background: T.cardBg,
                  border: `1px solid ${T.border}`,
                  borderRadius: T.radius,
                  padding: "28px",
                }}
              >
                <p
                  className="text-xs font-bold uppercase tracking-widest mb-3"
                  style={{ color: T.accent, letterSpacing: "0.1em" }}
                >
                  Guided Economy
                </p>
                <p
                  className="font-bold mb-1"
                  style={{
                    color: T.textPrimary,
                    fontSize: "1.5rem",
                    fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
                  }}
                >
                  $900 / month
                </p>
                <p
                  className="text-sm font-medium mb-4"
                  style={{ color: T.accent }}
                >
                  You run it
                </p>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: T.textSecondary }}
                >
                  You set your own rates and run your own campaigns. We answer
                  inside 4 hours, and we run one strategy call each quarter.
                </p>
              </div>

              <div
                className="order-2 md:order-1"
                style={{
                  background: T.cardBg,
                  border: `1px solid ${T.border}`,
                  borderRadius: T.radius,
                  padding: "28px",
                }}
              >
                <p
                  className="text-xs font-bold uppercase tracking-widest mb-3"
                  style={{ color: T.textMuted, letterSpacing: "0.1em" }}
                >
                  Managed Economy
                </p>
                <p
                  className="text-sm font-medium mb-4"
                  style={{ color: T.textSecondary }}
                >
                  We run it
                </p>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: T.textSecondary }}
                >
                  A dedicated Economist sets your rates, tunes the economy every
                  quarter, and delivers the Economy Review. 16 hours a month
                  included.
                </p>
              </div>
            </div>

            <p
              className="text-sm text-center"
              style={{ color: T.textMuted, fontStyle: "italic" }}
            >
              The platform is identical. You are choosing who does the work.
            </p>

            <div className="flex justify-center mt-6">
              <button
                type="button"
                onClick={() => navigate("/managed")}
                style={{
                  background: "transparent",
                  border: `1px solid ${T.accent}`,
                  borderRadius: "99px",
                  padding: "10px 24px",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: T.accent,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = T.accentDim; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                See Managed Economy →
              </button>
            </div>
          </SectionWrap>

          <Divider />

          {/* ── Block 3 — Labour split ───────────────────────────────────── */}
          <SectionWrap>
            <h2
              className="font-semibold mb-8"
              style={{
                color: T.textPrimary,
                fontSize: "1.25rem",
                fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
              }}
            >
              Who does what
            </h2>

            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  minWidth: "520px",
                  borderCollapse: "collapse",
                  fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
                }}
              >
                <thead>
                  <tr>
                    {["Work", "Guided", "Managed"].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: h === "Work" ? "left" : "center",
                          padding: "10px 16px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: T.textMuted,
                          borderBottom: `1px solid ${T.border}`,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {LABOUR.map((row, i) => (
                    <tr
                      key={i}
                      style={{
                        borderBottom: i < LABOUR.length - 1 ? `1px solid ${T.border}` : "none",
                      }}
                    >
                      <td
                        style={{
                          padding: "14px 16px",
                          fontSize: "0.9375rem",
                          color: T.textSecondary,
                        }}
                      >
                        {row.work}
                      </td>

                      {/* Guided cell */}
                      <td style={{ textAlign: "center", padding: "14px 16px" }}>
                        {row.guided === "Pabloo" ? (
                          <span
                            style={{
                              display: "inline-block",
                              background: T.accentDim,
                              color: T.accent,
                              fontSize: "0.8125rem",
                              fontWeight: 600,
                              padding: "3px 12px",
                              borderRadius: "99px",
                            }}
                          >
                            Pabloo
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: "0.8125rem",
                              color: T.textMuted,
                            }}
                          >
                            You
                          </span>
                        )}
                      </td>

                      {/* Managed cell */}
                      <td style={{ textAlign: "center", padding: "14px 16px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            background: T.accentDim,
                            color: T.accent,
                            fontSize: "0.8125rem",
                            fontWeight: 600,
                            padding: "3px 12px",
                            borderRadius: "99px",
                          }}
                        >
                          Pabloo
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionWrap>

          <Divider />

          {/* ── Block 4 — What is included ──────────────────────────────── */}
          <SectionWrap>
            <h2
              className="font-semibold mb-8"
              style={{
                color: T.textPrimary,
                fontSize: "1.25rem",
                fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
              }}
            >
              What is included
            </h2>

            <div
              style={{
                background: T.cardBg,
                border: `1px solid ${T.border}`,
                borderRadius: T.radius,
                overflow: "hidden",
                marginBottom: "20px",
              }}
            >
              {[
                { meter: "Customer accounts",       value: "120,000" },
                { meter: "Store accounts",          value: "20" },
                { meter: "Team accounts",           value: "Unlimited" },
                { meter: "Credit events each month",value: "800,000" },
                { meter: "Credit issued",           value: "Unlimited" },
                { meter: "API access",              value: "Full" },
                { meter: "Support response",        value: "Inside 4 hours" },
                { meter: "Strategy call",           value: "One each quarter" },
              ].map((row, i, arr) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "14px 24px",
                    borderBottom:
                      i < arr.length - 1 ? `1px solid ${T.border}` : "none",
                    fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
                  }}
                >
                  <span style={{ color: T.textSecondary, fontSize: "0.9375rem" }}>
                    {row.meter}
                  </span>
                  <span
                    style={{
                      color: T.textPrimary,
                      fontSize: "0.9375rem",
                      fontWeight: 500,
                    }}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>

            <p
              className="text-sm leading-relaxed mb-3"
              style={{ color: T.textMuted }}
            >
              A customer account is one shopper with a credit balance above zero.
              Wallets that empty out stop counting on your next bill.
            </p>
            <p className="text-sm leading-relaxed" style={{ color: T.textMuted }}>
              Credit issued is never metered. Issue ten dollars or ten million.
              The price does not change.
            </p>
          </SectionWrap>

          <Divider />

          {/* ── Block 5 — Upgrade path ───────────────────────────────────── */}
          <SectionWrap>
            <div
              style={{
                background: T.cardBg,
                border: `1px solid ${T.border}`,
                borderRadius: "20px",
                padding: "40px",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <div>
                <p
                  className="text-xs font-bold uppercase tracking-widest mb-3"
                  style={{ color: T.accent, letterSpacing: "0.12em", fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif" }}
                >
                  Ready to hand it off?
                </p>
                <h2
                  className="font-semibold mb-3"
                  style={{
                    color: T.textPrimary,
                    fontSize: "1.25rem",
                    fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
                  }}
                >
                  Move to Managed whenever you want.
                </h2>
                <p
                  style={{
                    color: T.textSecondary,
                    fontSize: "0.9375rem",
                    lineHeight: 1.7,
                    maxWidth: "480px",
                    fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
                  }}
                >
                  Guided and Managed run on the same ledger. Moving takes one call
                  and no migration: your history, rules, and balances stay exactly
                  where they are.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/managed")}
                style={{
                  alignSelf: "flex-start",
                  background: T.accent,
                  color: "#0f0f10",
                  border: "none",
                  borderRadius: "99px",
                  padding: "12px 28px",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
                  transition: "opacity 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
              >
                See Managed Economy
              </button>
            </div>
          </SectionWrap>

          <Divider />

          {/* ── Block 6 — Action ─────────────────────────────────────────── */}
          <SectionWrap style={{ paddingBottom: "96px" }}>
            {sent ? (
              <div className="flex flex-col items-center gap-4 text-center">
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    background: T.accentDim,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircle size={22} style={{ color: T.accent }} />
                </div>
                <p
                  className="font-semibold text-xl"
                  style={{
                    color: T.textPrimary,
                    fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
                  }}
                >
                  Sent.
                </p>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: T.textSecondary, maxWidth: "380px" }}
                >
                  The agreement is in your inbox. Reply to sign and we will book
                  the integration call.
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <button
                  type="button"
                  onClick={handleStart}
                  disabled={loading}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 font-bold text-sm transition-all"
                  style={{
                    background: loading ? T.accentDim : T.accent,
                    color: loading ? T.accent : "#0f0f10",
                    border: "none",
                    borderRadius: "99px",
                    padding: "14px 36px",
                    cursor: loading ? "not-allowed" : "pointer",
                    fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif",
                    fontSize: "0.9375rem",
                  }}
                >
                  {loading && <Loader2 size={14} className="animate-spin" />}
                  {loading ? "Sending..." : "Start Guided Economy"}
                </button>
                <p className="text-xs" style={{ color: T.textMuted }}>
                  We send the agreement today. Integration starts inside 5
                  business days.
                </p>
              </div>
            )}
          </SectionWrap>

        </motion.div>
      </div>
    </>
  );
}
